import React from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Button, Switch, Input, Alert, Spinner } from '@/components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

interface ScheduledJobConfig {
  jobType: string;
  enabled: boolean;
  intervalMinutes: number;
  lastRunAt: string | null;
}

interface JobCardProps {
  jobType: string;
  title: string;
  description: string;
}

function JobCard({ jobType, title, description }: JobCardProps) {
  const queryClient = useQueryClient();
  const [isRunningNow, setIsRunningNow] = React.useState(false);

  // Fetch config
  const { data: config, isLoading: loadingConfig } = useQuery<ScheduledJobConfig>({
    queryKey: ['admin', 'scheduled-jobs', jobType],
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/scheduled-jobs/${jobType}`);
      if (!res.ok) throw new Error('Failed to fetch scheduled job config');
      const json = await res.json();
      return json.success ? json.data : null;
    },
    staleTime: 30_000,
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async (updates: { enabled?: boolean; intervalMinutes?: number }) => {
      const res = await apiFetch(`/api/admin/scheduled-jobs/${jobType}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Failed to update config');
      }
      const json = await res.json();
      return json.success ? json.data : null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scheduled-jobs', jobType] });
    },
  });

  // Run now mutation
  const runNowMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/admin/scheduled-jobs/${jobType}/run-now`, {
        method: 'POST',
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Failed to trigger run');
      }
      const json = await res.json();
      return json.success ? json.data : null;
    },
    onSuccess: () => {
      setIsRunningNow(false);
      // Refresh config to update lastRunAt
      queryClient.invalidateQueries({ queryKey: ['admin', 'scheduled-jobs', jobType] });
    },
    onError: () => {
      setIsRunningNow(false);
    },
  });

  const handleToggleEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!config) return;
    updateConfigMutation.mutate({ enabled: e.target.checked });
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = parseInt(e.target.value, 10);
    if (isNaN(minutes) || minutes < 1 || minutes > 1440) return;
    updateConfigMutation.mutate({ intervalMinutes: minutes });
  };

  const handleRunNow = () => {
    setIsRunningNow(true);
    runNowMutation.mutate();
  };

  if (loadingConfig) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" label="Loading..." />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!config) {
    return (
      <Card>
        <CardContent>
          <div className="text-sm text-gray-500">Configuration not found</div>
        </CardContent>
      </Card>
    );
  }

  const nextRunTime = config.lastRunAt && config.enabled
    ? new Date(new Date(config.lastRunAt).getTime() + config.intervalMinutes * 60 * 1000)
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">Status</div>
              <div className="text-sm text-gray-500">
                {config.enabled ? 'Enabled - Running automatically' : 'Disabled - Not running'}
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onChange={handleToggleEnabled}
              disabled={updateConfigMutation.isPending}
              aria-label={`Enable scheduled ${title}`}
            />
          </div>

          {/* Interval Setting */}
          <div>
            <Input
              type="number"
              label="Interval (minutes)"
              min="1"
              max="1440"
              value={config.intervalMinutes.toString()}
              onChange={handleIntervalChange}
              disabled={updateConfigMutation.isPending || !config.enabled}
              hint={`How often to run this job. Minimum: 1 minute, Maximum: 1440 minutes (24 hours). Currently set to ${config.intervalMinutes} minute${config.intervalMinutes !== 1 ? 's' : ''}.`}
              className="w-32"
            />
          </div>

          {/* Last Run & Next Run Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <div className="text-sm font-medium text-gray-900 mb-1">Last Run</div>
              <div className="text-sm text-gray-600">
                {config.lastRunAt 
                  ? new Date(config.lastRunAt).toLocaleString()
                  : 'Never'}
              </div>
            </div>
            {nextRunTime && (
              <div>
                <div className="text-sm font-medium text-gray-900 mb-1">Next Run</div>
                <div className="text-sm text-gray-600">
                  {nextRunTime.toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* Run Now Button */}
          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={handleRunNow}
              disabled={isRunningNow || runNowMutation.isPending}
              variant="primary"
              isLoading={isRunningNow || runNowMutation.isPending}
            >
              Run Now
            </Button>
            {runNowMutation.isSuccess && (
              <Alert variant="success" className="mt-4">
                {runNowMutation.data?.message || 'Job enqueued successfully'}
                {runNowMutation.data?.enqueuedCount && (
                  <span className="ml-2">
                    ({runNowMutation.data.enqueuedCount} of {runNowMutation.data.totalAccounts} accounts)
                  </span>
                )}
              </Alert>
            )}
            {runNowMutation.isError && (
              <Alert variant="error" className="mt-4">
                {runNowMutation.error instanceof Error ? runNowMutation.error.message : 'Failed to trigger run'}
              </Alert>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ScheduledJobs() {
  return (
    <PageContainer>
      <PageHeader
        title="Scheduled Jobs"
        description="Manage scheduled background jobs that run automatically"
      />

      <div className="mt-6 space-y-6">
        <JobCard
          jobType="boilerplate_job"
          title="Boilerplate Job"
          description="A simple boilerplate scheduled job that can be extended for future scheduled tasks. This serves as a template for creating new scheduled jobs."
        />
      </div>
    </PageContainer>
  );
}


